import React, { useState, useEffect } from 'react';
import { X, Clock, User, Calendar, DollarSign, Tag, CheckCircle, AlertCircle, Play, FileText, Copy, ExternalLink, MessageSquare, Plus } from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';
import { ClientNotes } from '../clients/ClientNotes';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { TaskReallocationHistory } from './TaskReallocationHistory';
import { RecurrenceBadge } from '../../components/ui/RecurrenceBadge';
import { FutureDueDates } from '../../components/ui/FutureDueDates';

interface UserTaskDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string | null;
}

interface TaskDetails {
  id: string;
  type: string;
  title: string;
  description?: string;
  clientId: string;
  clientName?: string;
  status: string;
  documentsRepositoryUrl?: string;
  assignees: {
    preparerId?: string;
    reviewerId?: string;
    submitterId?: string;
  };
  assigneeNames?: {
    preparer?: string;
    reviewer?: string;
    submitter?: string;
  };
  schedule: {
    frequency: string;
    dueDate: string;
    periodStart?: string;
    periodEnd?: string;
  };
  checklist: Array<{
    _id: string;
    label: string;
    done: boolean;
    required: boolean;
    order: number;
    doneAt?: string;
    doneBy?: string;
  }>;
  timeSpentMinutes: number;
  billing: {
    feeType: string;
    fixedAmount?: number;
    billableRate?: number;
    estimatedHours?: number;
  };
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags: string[];
  completionPercentage: number;
  activityLog: Array<{
    at: string;
    by: string;
    action: string;
    description?: string;
    meta?: any;
  }>;
  reallocationHistory?: Array<{
    at: string;
    by: {
      id: string;
      fullName: string;
    };
    fromUser?: {
      id: string;
      fullName: string;
    };
    toUser: {
      id: string;
      fullName: string;
    };
    role: 'preparer' | 'reviewer' | 'submitter';
    comment?: string;
    reason?: string;
  }>;
  recurrenceInfo?: {
    isRecurring: boolean;
    description: string;
    nextDueDates: string[];
  };
  createdAt: string;
  updatedAt: string;
}

// Documents Repository Display Component
interface DocumentsRepositoryDisplayProps {
  url: string;
}

const DocumentsRepositoryDisplay: React.FC<DocumentsRepositoryDisplayProps> = ({ url }) => {
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy URL');
    }
  };

  const openUrl = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">Documents Repository</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={copyToClipboard}
            className="p-1 text-blue-600 hover:text-blue-800"
            title="Copy URL"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={openUrl}
            className="p-1 text-blue-600 hover:text-blue-800"
            title="Open URL"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      </div>
      <p className="text-sm text-blue-700 mt-1 break-all">{url}</p>
    </div>
  );
};

export const UserTaskDetailsModal: React.FC<UserTaskDetailsModalProps> = ({
  isOpen,
  onClose,
  taskId
}) => {
  const [loading, setLoading] = useState(false);
  const [task, setTask] = useState<TaskDetails | null>(null);

  useEffect(() => {
    if (isOpen && taskId) {
      fetchTaskDetails();
    }
  }, [isOpen, taskId]);

  const fetchTaskDetails = async () => {
    if (!taskId) return;

    setLoading(true);
    try {
      const response = await api.get(`/tasks/${taskId}`);
      if (response.data.success) {
        setTask(response.data.data);
      } else {
        console.error('API returned unsuccessful response:', response.data);
        toast.error(response.data.message || 'Failed to load task details');
      }
    } catch (error: any) {
      console.error('Error fetching task details:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load task details';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'submitted':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Play className="w-5 h-5 text-indigo-500" />;
      case 'in_review':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'ready_to_submit':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'draft':
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Task Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : task ? (
          <div className="p-6">
            {/* Task Header */}
            <div className="mb-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{task.title || 'Untitled Task'}</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(task.status)}
                      <span className="text-sm font-medium">
                        {task.status?.replace('_', ' ') || 'Unknown Status'}
                      </span>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(task.priority)}`}>
                      {task.priority ? task.priority.charAt(0).toUpperCase() + task.priority.slice(1) : 'Medium'} Priority
                    </span>
                    <span className="text-sm text-gray-500">
                      {task.type?.replace('_', ' ') || 'Unknown Type'}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">Due Date</div>
                  <div className="font-medium">
                    {task.schedule?.dueDate ? new Date(task.schedule.dueDate).toLocaleDateString() : 'Not set'}
                  </div>
                </div>
              </div>

              {task.description && (
                <p className="text-gray-700 mb-4">{task.description}</p>
              )}

              {/* Documents Repository URL */}
              {task.documentsRepositoryUrl && (
                <DocumentsRepositoryDisplay url={task.documentsRepositoryUrl} />
              )}

              {task.tags && task.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {task.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Tabs */}
            <Tabs defaultValue="details" className="w-full">
              <TabsList className={`grid w-full ${task.reallocationHistory && task.reallocationHistory.length > 0 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <TabsTrigger value="details" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Task Details
                </TabsTrigger>
                <TabsTrigger value="notes" className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Client Notes
                </TabsTrigger>
                {task.reallocationHistory && task.reallocationHistory.length > 0 && (
                  <TabsTrigger value="reallocation" className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Reallocation History ({task.reallocationHistory.length})
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="details" className="space-y-6">

                {/* Task Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Client Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Client</h4>
                    <p className="text-gray-700">{task.clientName || 'Unknown Client'}</p>
                  </div>

                  {/* Time Tracking */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Time Tracking</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Spent:</span>
                        <span className="font-medium">{formatTime(task.timeSpentMinutes || 0)}</span>
                      </div>
                      {task.billing?.estimatedHours && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Estimated:</span>
                          <span className="font-medium">{task.billing.estimatedHours}h</span>
                        </div>
                      )}
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full"
                          style={{
                            width: `${Math.min(task.completionPercentage || 0, 100)}%`
                          }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 text-center">
                        {task.completionPercentage || 0}% Complete
                      </div>
                    </div>
                  </div>

                  {/* Billing Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Billing</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Type:</span>
                        <span className="font-medium">{task.billing?.feeType || 'Not specified'}</span>
                      </div>
                      {task.billing?.fixedAmount && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Fixed:</span>
                          <span className="font-medium">${task.billing.fixedAmount}</span>
                        </div>
                      )}
                      {task.billing?.billableRate && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Rate:</span>
                          <span className="font-medium">${task.billing.billableRate}/hr</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Schedule Information */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Schedule & Recurrence</h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <div className="text-sm text-gray-600">Frequency</div>
                        <div className="font-medium">{task.schedule?.frequency?.replace('_', ' ') || 'Not specified'}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Due Date</div>
                        <div className="font-medium">
                          {task.schedule?.dueDate ? new Date(task.schedule.dueDate).toLocaleDateString() : 'Not set'}
                        </div>
                      </div>
                      {task.schedule?.periodStart && (
                        <div>
                          <div className="text-sm text-gray-600">Period Start</div>
                          <div className="font-medium">
                            {new Date(task.schedule.periodStart).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Recurrence Information */}
                    {task.recurrenceInfo && (
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <RecurrenceBadge
                            recurrenceInfo={task.recurrenceInfo}
                            size="md"
                            showDescription={true}
                          />
                        </div>

                        {task.recurrenceInfo.isRecurring && task.recurrenceInfo.nextDueDates.length > 0 && (
                          <FutureDueDates
                            nextDueDates={task.recurrenceInfo.nextDueDates}
                            maxDates={5}
                          />
                        )}
                      </div>
                    )}

                    {task.schedule?.periodEnd && (
                      <div>
                        <div className="text-sm text-gray-600">Period End</div>
                        <div className="font-medium">
                          {new Date(task.schedule.periodEnd).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Assignees */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Assignees</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium">Preparer</div>
                        <div className="text-sm text-gray-600">
                          {task.assigneeNames?.preparer || 'Unassigned'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium">Reviewer</div>
                        <div className="text-sm text-gray-600">
                          {task.assigneeNames?.reviewer || 'Unassigned'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium">Submitter</div>
                        <div className="text-sm text-gray-600">
                          {task.assigneeNames?.submitter || 'Unassigned'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Checklist */}
                {task.checklist && task.checklist.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Checklist</h4>
                    <div className="space-y-2">
                      {task.checklist.map((item) => (
                        <div key={item._id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={item.done}
                            readOnly
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className={`flex-1 ${item.done ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                            {item.label}
                          </span>
                          {item.required && (
                            <span className="text-xs text-red-600">Required</span>
                          )}
                          {item.done && item.doneAt && (
                            <span className="text-xs text-gray-500">
                              Completed {new Date(item.doneAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Activity Log */}
                {task.activityLog && Array.isArray(task.activityLog) && task.activityLog.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Activity Log</h4>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {task.activityLog.map((activity, index) => (
                        <div key={index} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-2 h-2 bg-primary-600 rounded-full mt-2"></div>
                          <div className="flex-1">
                            <div className="text-sm text-gray-900">
                              {activity.description || activity.action}
                            </div>
                            {activity.meta?.reason && (
                              <div className="mt-2 p-2 bg-orange-50 rounded border-l-2 border-orange-200">
                                <span className="text-xs font-medium text-orange-800">Rework Reason:</span>
                                <span className="text-xs text-orange-700 ml-1">"{activity.meta.reason}"</span>
                              </div>
                            )}
                            <div className="text-xs text-gray-500">
                              {activity.by} • {new Date(activity.at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Task Metadata */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Task Information</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-600">Created</div>
                        <div className="font-medium">
                          {new Date(task.createdAt).toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Last Updated</div>
                        <div className="font-medium">
                          {new Date(task.updatedAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="notes">
                {/* Client Notes */}
                {task.clientId && (
                  <div>
                    <ClientNotes
                      clientId={task.clientId}
                      clientName={task.clientName}
                      showHeader={false}
                      showAddButton={true}
                      maxHeight="400px"
                    />
                  </div>
                )}
              </TabsContent>

              {task.reallocationHistory && task.reallocationHistory.length > 0 && (
                <TabsContent value="reallocation">
                  <TaskReallocationHistory
                    reallocationHistory={task.reallocationHistory}
                    className="mt-4"
                  />
                </TabsContent>
              )}
            </Tabs>
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500">
            Task not found
          </div>
        )}
      </div>
    </div>
  );
};
